import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import SiteCard from '../../components/SiteCard';
import SEOComponent from '../../components/SEOComponent';

const Methodology = ({ url }) => {
  return (
    <>
      <SEOComponent 
        title={'ottaproperty.co.uk Methodology'}
        description={'How the price index is created'}
        url={url}
        imageUrl={'https://ottaproperty.co.uk/blog_images/how_to/England.png'}
      />
      <Container>
        <SiteCard 
          header={'ottaproperty.co.uk index'}
          title={'Methodology'}
          content={
            <Container>    
              <Row>
                <Col md={10} style={{margin: 'auto'}}>
                <img src="blog_images/july_index/england_years.png" alt="England" style={{width: '100%'}}/>
                  <h2>The ONS as The Starting Point</h2>
                  <hr/>
                  <p>
                    The Office for National Statistics builds their house price index using hedonic regression - treating each property as a collection of features that affect its value. They look at location, property type, number of rooms, floor area and other attributes. We use the same fundamental approach but with gradient boosting, a machine learning technique that can capture non-linear relationships and complex interactions between features.
                  </p>

                  <h2>A Slightly Different Approach</h2>
                  <hr/>
                  <p>
                    We've kept the fundamentals that make the ONS method work - mix-adjustment and chain linking. The difference is we use gradient boosting instead of linear regression. This means the model can discover patterns automatically - like how the value of extra floor space might depend on both the property type and location, or how being 500 meters closer to a certain area affects prices differently for flats versus houses.
                  </p>

                  <h2>How It Actually Works</h2>
                  <hr/>
                  <p>First, we prepare the data:</p>
                <ul>
                    <li>Merge Land Registry price data with Energy Performance Certificates to get floor areas</li>
                    <li>Match properties using their address and postcode</li>
                    <li>Add latitude and longitude coordinates from UK postcode data</li>
                    <li>Transform prices to log scale for better model training</li>
                  </ul>

                  <p>Then we train the models:</p>
                  <ul>
                    <li>For each month, train multiple gradient boosting models (10 for historical data, 100 for recent months)</li>
                    <li>Each model uses different random splits (80% training, 20% testing)</li>
                    <li>Features used: property type, latitude/longitude, floor area, new/old status, and freehold/leasehold</li>
                    <li>Models learn to predict log prices, then we convert back to actual prices</li>
                  </ul>

                  <p>To create the index:</p>
                  <ul>
                    <li>Create annual baskets - all properties sold in a given year</li>
                    <li>Each month's models predict prices for last year's properties (what would they sell for now?)</li>
                    <li>January uses two baskets (current year and previous year) for chain-linking</li>
                    <li>Average predictions across all models using geometric mean</li>
                    <li>Apply chain-linking ratios to make different basket years comparable</li>
                  </ul>

                  <h2>Getting Location Right</h2>
                  <hr/>
                  <p>
                    Instead of working with postcode districts or predefined geographical areas, we use actual latitude and longitude coordinates. The coordinates are rounded slightly to prevent overfitting to exact locations while still capturing local effects. This means the model can discover micro-location patterns on its own - like how prices change as you move across a neighbourhood, or how proximity to certain features affects different property types. The algorithm learns these geographic patterns from the data rather than us having to define boundaries in advance.
                  </p>

                  <h2>Why Multiple Models?</h2>
                  <hr/>
                  <p>
                    Instead of training one model per month, we train an ensemble:
                  </p>
                  <ul>
                    <li>100 gradient boosting models for recent months</li>
                    <li>10 models for historical data</li>
                    <li>Each model trains on a different random 80/20 split of the data</li>
                    <li>We average predictions across all models using geometric mean</li>
                    <li>The spread of predictions gives us natural confidence intervals</li>
                  </ul>
                  <p>
                    This ensemble approach reduces the impact of any single model's quirks and gives more robust estimates. It's similar to asking 100 experts for their opinion and taking the average rather than relying on just one expert.
                  </p>
                  <h3>Model performance</h3>
                                    <h5>An example month</h5>
                                    <img src="/blog_images/how_to/2006-01_model.png" alt="England" style={{width: '80%'}}/>



                  <h2>Adapting Over Time</h2>
                  <hr/>
                  <p>
                    Each month gets its own set of gradient boosting models trained on that month's transaction data. This means the index automatically adapts to changing market conditions - the models learn what matters for prices in each time period. Gradient boosting can capture complex interactions: for example, how the value of extra floor space might depend on both the property type and its location, or how being a new-build affects price differently in different areas. These patterns are learned from the data rather than being manually specified.
                  </p>

                </Col>
              </Row>
            </Container>
          }
        />
      </Container>
    </>
  );
};

export default Methodology;